import { TECHS } from "./research";

/**
 * Helper to update resource store values, supporting both simple numbers
 * (e.g., resources.food = 10) and object format (e.g., resources.food = { stored: 10, max: 100 }).
 * Also handles aliases like refined_metal / metal and raw_water / water.
 */
export function addResourceToStore(resources, key, amount) {
  if (!resources) return;

  const aliases = [key];
  if (key === "refined_metal") aliases.push("metal");
  if (key === "metal") aliases.push("refined_metal");
  if (key === "raw_water") aliases.push("water");
  if (key === "water") aliases.push("raw_water");

  let updated = false;

  for (const k of aliases) {
    if (resources[k] !== undefined) {
      if (typeof resources[k] === "number") {
        resources[k] += amount;
        updated = true;
      } else if (typeof resources[k] === "object" && resources[k] !== null) {
        const current = resources[k].stored ?? 0;
        const max = resources[k].max ?? Infinity;
        resources[k].stored = Math.min(max, current + amount);
        updated = true;
      }
    }
  }

  if (!updated) {
    resources[key] = amount;
  }
}

export const EVENTS = [
  {
    id: "meteor_shower",
    name: "Meteor Shower",
    description: "Meteor shower detected!",
    severity: "bad",
    minSol: 2,
    weight: 10,
    effect: (state) => {
      const buildings = state?.buildings ? state.buildings.map((b) => ({ ...b })) : [];
      let damagedBuilding = null;
      let targetName = "facility";

      if (buildings.length > 0) {
        const idx = Math.floor(Math.random() * buildings.length);
        const currentCond = typeof buildings[idx].condition === "number" ? buildings[idx].condition : 1.0;
        const newCond = Math.max(0, Math.round((currentCond - 0.2) * 100) / 100);
        buildings[idx].condition = newCond;
        damagedBuilding = buildings[idx];
        targetName = buildings[idx].name || buildings[idx].label || buildings[idx].type || "facility";
      }

      return {
        text: "Meteor shower detected!",
        severity: "bad",
        buildings: buildings.length > 0 ? buildings : undefined,
        damagedBuildingId: damagedBuilding?.id,
        damagedBuildingName: targetName,
        buildingConditionDelta: -0.2,
      };
    },
  },
  {
    id: "solar_flare",
    name: "Solar Flare",
    description: "Solar flare — solar panels disrupted!",
    severity: "bad",
    minSol: 2,
    weight: 10,
    effect: (state) => {
      return {
        text: "Solar flare — solar panels disrupted!",
        severity: "bad",
        duration: 30,
        modifier: {
          type: "solar_output",
          factor: 0.5,
          duration: 30,
        },
        solarMultiplier: 0.5,
      };
    },
  },
  {
    id: "dust_devil",
    name: "Dust Devil",
    description: "Dust devil near construction site!",
    severity: "bad",
    minSol: 1,
    weight: 12,
    effect: (state) => {
      return {
        text: "Dust devil near construction site!",
        severity: "bad",
        duration: 20,
        modifier: {
          type: "drone_speed",
          factor: 0.5,
          duration: 20,
        },
        droneSpeedMultiplier: 0.5,
      };
    },
  },
  {
    id: "supply_drop",
    name: "Supply Cache",
    description: "Unexpected supply cache discovered!",
    severity: "good",
    minSol: 1,
    weight: 15,
    effect: (state) => {
      const resources = state?.resources ? JSON.parse(JSON.stringify(state.resources)) : {};
      addResourceToStore(resources, "refined_metal", 50);
      addResourceToStore(resources, "food", 30);

      return {
        text: "Unexpected supply cache discovered!",
        severity: "good",
        resources,
        changes: { refined_metal: 50, food: 30 },
      };
    },
  },
  {
    id: "colonist_breakthrough",
    name: "Colonist Breakthrough",
    description: "Colonist research breakthrough!",
    severity: "good",
    minSol: 3,
    weight: 8,
    effect: (state) => {
      let colonistCount = 0;
      if (Array.isArray(state?.colonists)) {
        colonistCount = state.colonists.length;
      } else if (typeof state?.colonists === "number") {
        colonistCount = state.colonists;
      } else if (typeof state?.colonistCount === "number") {
        colonistCount = state.colonistCount;
      }

      const bonus = colonistCount > 0 ? colonistCount * 20 : 20;
      const resources = state?.resources ? JSON.parse(JSON.stringify(state.resources)) : {};
      addResourceToStore(resources, "credits", bonus);

      return {
        text: "Colonist research breakthrough!",
        severity: "good",
        resources,
        changes: { credits: bonus },
        colonistCount,
        creditBonusPerColonist: 20,
      };
    },
  },
  {
    id: "equipment_malfunction",
    name: "Equipment Malfunction",
    description: "Equipment malfunction at facility!",
    severity: "bad",
    minSol: 2,
    weight: 10,
    effect: (state) => {
      const buildings = state?.buildings ? state.buildings.map((b) => ({ ...b })) : [];
      let targetName = "facility";
      let disabledBuildingId = null;

      if (buildings.length > 0) {
        const activeIndices = buildings
          .map((b, i) => (b.active !== false && b.state !== "disabled" ? i : -1))
          .filter((i) => i !== -1);

        const idx = activeIndices.length > 0
          ? activeIndices[Math.floor(Math.random() * activeIndices.length)]
          : Math.floor(Math.random() * buildings.length);

        buildings[idx].active = false;
        buildings[idx].state = "disabled";
        disabledBuildingId = buildings[idx].id;
        targetName = buildings[idx].name || buildings[idx].label || buildings[idx].type || "facility";
      }

      const text = `Equipment malfunction at ${targetName}!`;

      return {
        text,
        severity: "bad",
        buildings: buildings.length > 0 ? buildings : undefined,
        disabledBuildingId,
        disabledBuildingName: targetName,
      };
    },
  },
  {
    id: "ice_deposit",
    name: "Ice Deposit Discovered",
    description: "Ice deposit discovered beneath surface!",
    severity: "good",
    minSol: 1,
    weight: 12,
    effect: (state) => {
      const resources = state?.resources ? JSON.parse(JSON.stringify(state.resources)) : {};
      addResourceToStore(resources, "raw_water", 100);

      return {
        text: "Ice deposit discovered beneath surface!",
        severity: "good",
        resources,
        changes: { raw_water: 100 },
      };
    },
  },
  {
    id: "sandstorm",
    name: "Major Sandstorm",
    description: "Major sandstorm — operations reduced!",
    severity: "bad",
    minSol: 3,
    weight: 8,
    effect: (state) => {
      return {
        text: "Major sandstorm — operations reduced!",
        severity: "bad",
        duration: 45,
        modifier: {
          type: "production",
          factor: 0.7,
          duration: 45,
        },
        productionMultiplier: 0.7,
      };
    },
  },
  {
    id: "power_surge",
    name: "Power Surge",
    description: "Power surge from reactor!",
    severity: "good",
    minSol: 1,
    weight: 15,
    effect: (state) => {
      const resources = state?.resources ? JSON.parse(JSON.stringify(state.resources)) : {};
      addResourceToStore(resources, "power", 50);

      return {
        text: "Power surge from reactor!",
        severity: "good",
        resources,
        changes: { power: 50 },
      };
    },
  },
  {
    id: "microbe_find",
    name: "Microbial Life Discovered",
    description: "Microbial life discovered — research boost!",
    severity: "good",
    minSol: 4,
    weight: 6,
    effect: (state) => {
      const completed = state?.research?.completed ? [...state.research.completed] : [];
      let unlockedTech = null;

      if (Array.isArray(TECHS)) {
        const available = TECHS.filter((t) => !completed.includes(t.id));
        if (available.length > 0) {
          unlockedTech = available[Math.floor(Math.random() * available.length)];
          completed.push(unlockedTech.id);
        }
      }

      const research = state?.research
        ? { ...state.research, completed }
        : { completed: unlockedTech ? [unlockedTech.id] : [] };

      return {
        text: "Microbial life discovered — research boost!",
        severity: "good",
        research,
        unlockedTechId: unlockedTech ? unlockedTech.id : null,
        unlockedTechName: unlockedTech ? unlockedTech.name : null,
      };
    },
  },
];

export const EVENTS_MAP = Object.fromEntries(EVENTS.map((e) => [e.id, e]));

export default EVENTS;
